/**
 * ActionsMenu — the ⋯ overflow menu for destructive + secondary header actions.
 * Hand-rolled: useState, no new deps.
 * Destructive items styled text-sem-alert, separated by a border-t.
 * [Source: v0 kunden-view.tsx DropdownMenu "Aktionen" + K4 Löschen nur in ⋯]
 */
import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/cn';
import type { LucideIcon } from 'lucide-react';

export interface ActionItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  destructive?: boolean;
  separator?: boolean; // renders a divider before this item
  /**
   * DMS Part2 P2-13: business-rule gate DISTINCT from permission gating — a caller
   * without the permission omits the item entirely (existing convention, e.g.
   * DocumentsScreen §ds-7); `disabled` is for a permitted item that is temporarily
   * unavailable (e.g. "Löschen" while the underlying document is still active —
   * dms/spec-part2-stefan-delta.md §1.3). `disabledReason` renders as the native
   * `title` tooltip explaining why.
   */
  disabled?: boolean;
  disabledReason?: string;
}

interface ActionsMenuProps {
  actions: ActionItem[];
  label?: string;
}

export function ActionsMenu({ actions, label = 'Aktionen' }: ActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="icon"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="size-9"
      >
        <MoreHorizontal className="size-4" />
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-48 rounded-md border bg-popover p-1 shadow-md"
        >
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.label}>
                {a.separator && <div className="my-1 border-t border-border" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={a.disabled}
                  title={a.disabled ? a.disabledReason : undefined}
                  aria-disabled={a.disabled}
                  onClick={() => {
                    if (a.disabled) return;
                    a.onClick();
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                    a.destructive && 'text-sem-alert hover:bg-sem-alert-bg hover:text-sem-alert',
                    a.disabled &&
                      'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-current',
                  )}
                >
                  {Icon && <Icon className="size-3.5 shrink-0" />}
                  {a.label}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
